import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const RACE_TYPES = ["road", "trail", "triathlon"];
const TRIATHLON_TYPES = ["sprint", "olympic", "70.3", "ironman"];

const EMPTY_FORM = {
    name: "",
    trainingStartDate: "",
    raceDate: "",
    raceType: "road",
    distance: "",
    time: "",
    triathlonType: "sprint",
    swimTime: "",
    bikeTime: "",
    runTime: "",
    notes: "",
};

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
};

const inputStyle = {
    background: "#0d0d1a",
    border: "1px solid #2a2a4a",
    borderRadius: "6px",
    color: "#ddd",
    padding: "8px 10px",
    fontSize: "13px",
    width: "100%",
    boxSizing: "border-box",
};

const labelStyle = {
    fontSize: "12px",
    color: "#888",
    display: "block",
    marginBottom: "4px",
};

const sectionLabel = {
    fontSize: "11px",
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "12px",
};

// Sport types to display in stats (in order)
const STAT_SPORTS = ["Run", "Ride", "Swim"];
const SPORT_COLORS = { Run: "#00d4ff", Ride: "#4ecdc4", Swim: "#a855f7" };

function formatDate(str) {
    if (!str) return "—";
    const [y, m, d] = str.split("-");
    return `${m}/${d}/${y}`;
}

function typeColor(t) {
    if (t === "road") return "#00d4ff";
    if (t === "trail") return "#4ecdc4";
    if (t === "triathlon") return "#a855f7";
    return "#888";
}

function formatMiles(miles) {
    return miles.toFixed(1);
}

function formatDuration(totalSecs) {
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

function RaceResultSummary({ race }) {
    if (race.raceType === "triathlon") {
        const type = race.triathlonType ? `(${race.triathlonType})` : "";
        const parts = [
            race.swimTime && `Swim: ${race.swimTime}`,
            race.bikeTime && `Bike: ${race.bikeTime}`,
            race.runTime && `Run: ${race.runTime}`,
        ].filter(Boolean);
        return (
            <div style={{ fontSize: "12px", color: "#aaa", marginTop: "6px" }}>
                {type && <span style={{ marginRight: "8px", color: "#a855f7" }}>{type}</span>}
                {parts.join(" · ")}
            </div>
        );
    }
    const parts = [
        race.distance && race.distance,
        race.time && race.time,
    ].filter(Boolean);
    return parts.length > 0 ? (
        <div style={{ fontSize: "12px", color: "#aaa", marginTop: "6px" }}>{parts.join(" · ")}</div>
    ) : null;
}

function SportTile({ sport, data, color }) {
    const isSwim = sport === "Swim";
    const totalDisplay = isSwim
        ? `${Math.round(data.yards).toLocaleString()} yd`
        : `${formatMiles(data.miles)} mi`;
    const peakDisplay = isSwim
        ? (data.maxWeeklyYards > 0 ? `${Math.round(data.maxWeeklyYards).toLocaleString()} yd` : null)
        : (data.maxWeeklyMiles > 0 ? `${formatMiles(data.maxWeeklyMiles)} mi` : null);

    return (
        <div style={{
            background: "#0d0d1a",
            border: `1px solid ${color}22`,
            borderRadius: "8px",
            padding: "10px 14px",
            minWidth: "120px",
        }}>
            <div style={{ fontSize: "11px", color, marginBottom: "4px" }}>{sport}</div>
            <div style={{ fontSize: "15px", fontWeight: "bold", color: "#ddd" }}>{totalDisplay}</div>
            {peakDisplay && (
                <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>
                    Peak week: {peakDisplay}
                </div>
            )}
            <div style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}>
                {formatDuration(data.movingTimeSecs)} · {data.count} activities
            </div>
        </div>
    );
}

function TrainingStats({ stats }) {
    if (!stats) return null;

    const { byType, maxWeeklyRunMiles, totalMovingTimeSecs, activityCount } = stats;

    const sportRows = STAT_SPORTS.filter(s => byType[s]);
    const otherSports = Object.keys(byType).filter(s => !STAT_SPORTS.includes(s));

    return (
        <div style={{
            marginTop: "14px",
            paddingTop: "14px",
            borderTop: "1px solid #2a2a4a",
        }}>
            <div style={{ ...sectionLabel, marginBottom: "10px" }}>Training Block Stats</div>

            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "12px" }}>
                <div>
                    <div style={{ fontSize: "11px", color: "#555" }}>Total Activities</div>
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#ddd" }}>{activityCount}</div>
                </div>
                <div>
                    <div style={{ fontSize: "11px", color: "#555" }}>Total Time</div>
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#ddd" }}>{formatDuration(totalMovingTimeSecs)}</div>
                </div>
                {maxWeeklyRunMiles > 0 && (
                    <div>
                        <div style={{ fontSize: "11px", color: "#555" }}>Peak Run Week</div>
                        <div style={{ fontSize: "18px", fontWeight: "bold", color: SPORT_COLORS["Run"] }}>
                            {formatMiles(maxWeeklyRunMiles)} mi
                        </div>
                    </div>
                )}
            </div>

            {(sportRows.length > 0 || otherSports.length > 0) && (
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    {sportRows.map(sport => (
                        <SportTile key={sport} sport={sport} data={byType[sport]} color={SPORT_COLORS[sport]} />
                    ))}
                    {otherSports.map(sport => (
                        <SportTile key={sport} sport={sport} data={byType[sport]} color="#888" />
                    ))}
                </div>
            )}
        </div>
    );
}

const Races = () => {
    const navigate = useNavigate();
    const [races, setRaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Map of race._id -> stats object (null = loading, false = failed)
    const [statsMap, setStatsMap] = useState({});

    const getToken = useCallback(async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    }, []);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        fetchRaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const fetchRaces = async () => {
        setLoading(true);
        try {
            const token = await getToken();
            const { data } = await axios.get(`${base_url}/races`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRaces(data);
            // Fetch stats for all races in parallel
            fetchAllStats(data, token);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllStats = async (raceList, token) => {
        await Promise.all(raceList.map(async (race) => {
            if (!race.trainingStartDate || !race.raceDate) return;
            try {
                const { data } = await axios.get(`${base_url}/activity/block-stats`, {
                    params: { startDate: race.trainingStartDate, endDate: race.raceDate },
                    headers: { Authorization: `Bearer ${token}` },
                });
                setStatsMap(prev => ({ ...prev, [race._id]: data }));
            } catch {
                setStatsMap(prev => ({ ...prev, [race._id]: false }));
            }
        }));
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditId(null);
        setError(null);
        setShowForm(true);
    };

    const openEdit = (race) => {
        setForm({
            name: race.name || "",
            trainingStartDate: race.trainingStartDate || "",
            raceDate: race.raceDate || "",
            raceType: race.raceType || "road",
            distance: race.distance || "",
            time: race.time || "",
            triathlonType: race.triathlonType || "sprint",
            swimTime: race.swimTime || "",
            bikeTime: race.bikeTime || "",
            runTime: race.runTime || "",
            notes: race.notes || "",
        });
        setEditId(race._id);
        setError(null);
        setShowForm(true);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getToken();
            const payload = { ...form };
            if (payload.raceType !== "triathlon") {
                payload.triathlonType = undefined;
                payload.swimTime = undefined;
                payload.bikeTime = undefined;
                payload.runTime = undefined;
            } else {
                payload.distance = undefined;
                payload.time = undefined;
            }
            if (editId) {
                await axios.put(`${base_url}/races/${editId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${base_url}/races`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
            setShowForm(false);
            fetchRaces();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this race?")) return;
        try {
            const token = await getToken();
            await axios.delete(`${base_url}/races/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRaces(prev => prev.filter(r => r._id !== id));
            setStatsMap(prev => { const next = { ...prev }; delete next[id]; return next; });
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 40px", maxWidth: "860px", margin: "0 auto" }}>

                {/* Header row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <h2 style={{ color: "#00d4ff", margin: 0 }}>Races</h2>
                    {!showForm && (
                        <button
                            onClick={openCreate}
                            style={{
                                padding: "8px 18px",
                                background: "#00d4ff",
                                color: "#0d0d1a",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "13px",
                            }}
                        >
                            + Add Race
                        </button>
                    )}
                </div>

                {/* Form */}
                {showForm && (
                    <div style={{ ...card, marginBottom: "24px" }}>
                        <div style={{ ...sectionLabel, marginBottom: "16px" }}>
                            {editId ? "Edit Race" : "New Race"}
                        </div>
                        <form onSubmit={handleSave}>
                            <div style={{ marginBottom: "14px" }}>
                                <label style={labelStyle}>Race name</label>
                                <input
                                    style={inputStyle}
                                    value={form.name}
                                    onChange={e => handleChange("name", e.target.value)}
                                    placeholder="e.g. Boston Marathon 2026"
                                    required
                                />
                            </div>

                            <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                                <div style={{ flex: 1, minWidth: "160px" }}>
                                    <label style={labelStyle}>Training start date</label>
                                    <input
                                        type="date"
                                        style={inputStyle}
                                        value={form.trainingStartDate}
                                        onChange={e => handleChange("trainingStartDate", e.target.value)}
                                        required
                                    />
                                </div>
                                <div style={{ flex: 1, minWidth: "160px" }}>
                                    <label style={labelStyle}>Race date</label>
                                    <input
                                        type="date"
                                        style={inputStyle}
                                        value={form.raceDate}
                                        onChange={e => handleChange("raceDate", e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: "14px" }}>
                                <label style={labelStyle}>Race type</label>
                                <div style={{ display: "flex", gap: "8px" }}>
                                    {RACE_TYPES.map(t => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => handleChange("raceType", t)}
                                            style={{
                                                padding: "6px 14px",
                                                background: form.raceType === t ? "#2a2a4a" : "transparent",
                                                color: form.raceType === t ? typeColor(t) : "#555",
                                                border: `1px solid ${form.raceType === t ? typeColor(t) : "#2a2a4a"}`,
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {(form.raceType === "road" || form.raceType === "trail") && (
                                <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                                    <div style={{ flex: 1, minWidth: "160px" }}>
                                        <label style={labelStyle}>Distance</label>
                                        <input
                                            style={inputStyle}
                                            value={form.distance}
                                            onChange={e => handleChange("distance", e.target.value)}
                                            placeholder="e.g. 26.2 mi"
                                        />
                                    </div>
                                    <div style={{ flex: 1, minWidth: "160px" }}>
                                        <label style={labelStyle}>Finish time</label>
                                        <input
                                            style={inputStyle}
                                            value={form.time}
                                            onChange={e => handleChange("time", e.target.value)}
                                            placeholder="e.g. 3:45:22"
                                        />
                                    </div>
                                </div>
                            )}

                            {form.raceType === "triathlon" && (
                                <>
                                    <div style={{ marginBottom: "14px" }}>
                                        <label style={labelStyle}>Triathlon type</label>
                                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                            {TRIATHLON_TYPES.map(t => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => handleChange("triathlonType", t)}
                                                    style={{
                                                        padding: "6px 14px",
                                                        background: form.triathlonType === t ? "#2a2a4a" : "transparent",
                                                        color: form.triathlonType === t ? "#a855f7" : "#555",
                                                        border: `1px solid ${form.triathlonType === t ? "#a855f7" : "#2a2a4a"}`,
                                                        borderRadius: "6px",
                                                        cursor: "pointer",
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                                        <div style={{ flex: 1, minWidth: "120px" }}>
                                            <label style={labelStyle}>Swim time</label>
                                            <input
                                                style={inputStyle}
                                                value={form.swimTime}
                                                onChange={e => handleChange("swimTime", e.target.value)}
                                                placeholder="e.g. 32:14"
                                            />
                                        </div>
                                        <div style={{ flex: 1, minWidth: "120px" }}>
                                            <label style={labelStyle}>Bike time</label>
                                            <input
                                                style={inputStyle}
                                                value={form.bikeTime}
                                                onChange={e => handleChange("bikeTime", e.target.value)}
                                                placeholder="e.g. 1:12:05"
                                            />
                                        </div>
                                        <div style={{ flex: 1, minWidth: "120px" }}>
                                            <label style={labelStyle}>Run time</label>
                                            <input
                                                style={inputStyle}
                                                value={form.runTime}
                                                onChange={e => handleChange("runTime", e.target.value)}
                                                placeholder="e.g. 48:30"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div style={{ marginBottom: "16px" }}>
                                <label style={labelStyle}>Notes</label>
                                <textarea
                                    rows={3}
                                    style={{ ...inputStyle, resize: "vertical" }}
                                    value={form.notes}
                                    onChange={e => handleChange("notes", e.target.value)}
                                    placeholder="Optional notes about the race"
                                />
                            </div>

                            {error && (
                                <div style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>{error}</div>
                            )}

                            <div style={{ display: "flex", gap: "10px" }}>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        padding: "8px 20px",
                                        background: saving ? "#2a2a4a" : "#00d4ff",
                                        color: saving ? "#555" : "#0d0d1a",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: saving ? "not-allowed" : "pointer",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                    }}
                                >
                                    {saving ? "Saving..." : editId ? "Save Changes" : "Create Race"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowForm(false); setEditId(null); setError(null); }}
                                    style={{
                                        padding: "8px 16px",
                                        background: "transparent",
                                        color: "#555",
                                        border: "1px solid #2a2a4a",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Race list */}
                {loading ? (
                    <div style={{ color: "#555", fontSize: "13px" }}>Loading...</div>
                ) : races.length === 0 ? (
                    <div style={{ color: "#555", fontSize: "13px" }}>No races yet. Add one above.</div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {races.map(race => (
                            <div key={race._id} style={card}>
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                                    <div style={{ flex: 1, minWidth: "200px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                            <span style={{ fontSize: "15px", fontWeight: "bold", color: "#ddd" }}>{race.name}</span>
                                            <span style={{
                                                fontSize: "10px",
                                                padding: "2px 8px",
                                                borderRadius: "4px",
                                                border: `1px solid ${typeColor(race.raceType)}`,
                                                color: typeColor(race.raceType),
                                                textTransform: "capitalize",
                                            }}>
                                                {race.raceType}
                                            </span>
                                        </div>

                                        <div style={{ fontSize: "12px", color: "#555", marginTop: "6px" }}>
                                            Training: {formatDate(race.trainingStartDate)} → {formatDate(race.raceDate)}
                                        </div>

                                        <RaceResultSummary race={race} />

                                        {race.notes && (
                                            <div style={{ fontSize: "12px", color: "#666", marginTop: "6px", fontStyle: "italic" }}>
                                                {race.notes}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                                        <button
                                            onClick={() => openEdit(race)}
                                            style={{
                                                padding: "5px 12px",
                                                background: "transparent",
                                                color: "#00d4ff",
                                                border: "1px solid #00d4ff",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                            }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(race._id)}
                                            style={{
                                                padding: "5px 12px",
                                                background: "transparent",
                                                color: "#f87171",
                                                border: "1px solid #4a2a2a",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>

                                {/* Training block stats */}
                                {statsMap[race._id] === undefined && (
                                    <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px solid #2a2a4a", fontSize: "12px", color: "#444" }}>
                                        Loading stats...
                                    </div>
                                )}
                                {statsMap[race._id] === false && (
                                    <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px solid #2a2a4a", fontSize: "12px", color: "#555" }}>
                                        Could not load stats
                                    </div>
                                )}
                                {statsMap[race._id] && statsMap[race._id].activityCount === 0 && (
                                    <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px solid #2a2a4a", fontSize: "12px", color: "#555" }}>
                                        No Strava activities found in this training window
                                    </div>
                                )}
                                {statsMap[race._id] && statsMap[race._id].activityCount > 0 && (
                                    <TrainingStats stats={statsMap[race._id]} />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Races;
